import { IQuote, mapInsuranceQuoteTypeToOnlineServicesQuoteType } from '@/interfaces';
import { useLiveChatStore, useReferenceDataStore } from '@/store';

export enum loyaltyReviewContent {
  headingTriedToValidate = 'headingTriedToValidate',
  headingPrimaryHolderFound = 'headingPrimaryHolderFound',
  headingSecondaryHolderFound = 'headingSecondaryHolderFound',
  textMemberLevel = 'textMemberLevel',
  textMultiMemberLevel = 'textMultiMemberLevel',
  textRoadsideAdded = 'textRoadsideAdded',
  textNotAMember = 'textNotAMember',
  textNewQuoteCost = 'textNewQuoteCost',
  textHelpNoChat = 'textHelpNoChat',
  textHelpWithChat = 'textHelpWithChat',
  textDiscount = 'textDiscount',
}

const blankLoyaltyResponse = {
  skipLoyaltyReviewStep: true,
  useHeading: '',
  useLoyaltyText: '',
  useNewQuoteCostText: '',
  useHelpText: '',
};

export const displayLoyaltyReview = async (
  quote: IQuote,
  originalQuote?: IQuote,
  storyBookIgnoreApiMode: boolean = false
) => {
  const liveChatStore = useLiveChatStore();
  const refStore = useReferenceDataStore();

  if (!quote || !originalQuote) {
    return blankLoyaltyResponse;
  }

  const hasAdditionalPolicyholders = quote?.additionalPolicyHolderDetails?.length > 0;
  const highestLoyaltyLevelAfterPartyMatching = quote.discounts?.highestMembershipLoyaltyLevel.value;
  const eligibleForLoyaltyDiscount = quote.discounts?.eligibleForLoyaltyDiscount.value;
  const loyaltyLevelChanged = hasLoyaltyLevelChanged(quote, originalQuote);
  const quoteType = quote.quoteType ?? '';
  const onlineServicesQuoteType = mapInsuranceQuoteTypeToOnlineServicesQuoteType(quoteType as string);
  const levels = await refStore.getMembershipLevels(onlineServicesQuoteType);
  let matchedLevel = null;

  if (levels && levels?.length > 0) {
    matchedLevel = levels.find(
      (x: any) => x.code.toLowerCase() === highestLoyaltyLevelAfterPartyMatching?.toLowerCase()
    );
  }

  const loyaltyContent = getLoyaltyContent(
    storyBookIgnoreApiMode,
    eligibleForLoyaltyDiscount,
    matchedLevel,
    loyaltyLevelChanged,
    hasAdditionalPolicyholders
  );
  const shouldShowHelpText =
    originalQuote.insuranceQuoteSummary && quote.insuranceQuoteSummary
      ? originalQuote.insuranceQuoteSummary?.yearlyAnnualPremium < quote.insuranceQuoteSummary?.yearlyAnnualPremium
      : false;
  const chatHelpText = liveChatStore.liveChatEnabled
    ? loyaltyReviewContent.textHelpWithChat
    : loyaltyReviewContent.textHelpNoChat;
  const useHelpText = shouldShowHelpText ? chatHelpText : '';

  return {
    skipLoyaltyReviewStep: loyaltyLevelChanged.shouldSkip,
    useHeading: loyaltyContent.useHeading,
    useLoyaltyText: loyaltyContent.useLoyaltyText,
    useNewQuoteCostText: loyaltyReviewContent.textNewQuoteCost,
    useHelpText: useHelpText,
  };
};

const getLoyaltyContent = (
  storyBookIgnoreApiMode: boolean,
  eligibleForLoyaltyDiscount: boolean,
  matchedLevel: any,
  loyaltyLevelChanged: any,
  hasAdditionalPolicyholders: boolean
) => {
  let useHeading = '';
  let useLoyaltyText = '';

  if (
    (storyBookIgnoreApiMode || matchedLevel != null) &&
    loyaltyLevelChanged.hasRoadSideAfterPartyMatching &&
    eligibleForLoyaltyDiscount
  ) {
    if (loyaltyLevelChanged.loyaltyChanged || loyaltyLevelChanged.roadsideChanged) {
      if (!hasAdditionalPolicyholders) {
        useHeading = loyaltyReviewContent.headingPrimaryHolderFound;
        useLoyaltyText = loyaltyReviewContent.textMemberLevel;
      } else {
        useHeading = loyaltyReviewContent.headingSecondaryHolderFound;
        useLoyaltyText = loyaltyReviewContent.textMultiMemberLevel;
      }
    }
  } else {
    useHeading = loyaltyReviewContent.headingTriedToValidate;
    useLoyaltyText = loyaltyReviewContent.textNotAMember;
  }

  return {
    useHeading,
    useLoyaltyText,
  };
};

const hasLoyaltyLevelChanged = (quote: IQuote, originalQuote?: IQuote): any => {
  if (!originalQuote) return false;
  const highestLoyaltyLevelBeforePartyMatching = originalQuote.discounts?.highestMembershipLoyaltyLevel?.value;
  const highestLoyaltyLevelAfterPartyMatching = quote.discounts?.highestMembershipLoyaltyLevel?.value;

  const hasRoadSideBeforePartyMatching =
    (originalQuote.primaryPolicyHolderDetails?.hasRoadsideProduct?.value as boolean) ||
    originalQuote.additionalPolicyHolderDetails?.some((x) => x.hasRoadsideProduct?.value);

  const hasRoadSideAfterPartyMatching =
    (quote.primaryPolicyHolderDetails?.hasRoadsideProduct?.value as boolean) ||
    quote.additionalPolicyHolderDetails?.some((x) => x.hasRoadsideProduct?.value);

  const shouldSkip =
    (highestLoyaltyLevelBeforePartyMatching?.toLowerCase() === highestLoyaltyLevelAfterPartyMatching?.toLowerCase() &&
      hasRoadSideBeforePartyMatching === hasRoadSideAfterPartyMatching) ||
    (!hasRoadSideBeforePartyMatching && !hasRoadSideAfterPartyMatching);

  const response = {
    loyaltyChanged: highestLoyaltyLevelBeforePartyMatching != highestLoyaltyLevelAfterPartyMatching,
    roadsideChanged: hasRoadSideBeforePartyMatching != hasRoadSideAfterPartyMatching,
    shouldSkip: shouldSkip,
    highestLoyaltyLevelAfterPartyMatching: highestLoyaltyLevelAfterPartyMatching,
    hasRoadSideAfterPartyMatching: hasRoadSideAfterPartyMatching,
  };

  return response;
};

export const displayLoyaltyReviewUsingStore = async (iqStore: any) => {
  return await displayLoyaltyReview(iqStore.quote, iqStore.originalQuote);
};
